import React from 'react';
import { string, bool, oneOfType } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { propTypes } from '../../../util/types';
import css from './LeadFeeRequestForm.module.css';
import { Form, PrimaryButton, FieldTextInput, Heading, Avatar } from '../../../components';
import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import { isErrorNoPermissionForInitiateTransactions, isErrorNoPermissionForUserPendingApproval } from '../../../util/errors';

const MESSAGE_MAX_LENGTH = 500;

const ErrorMessage = props => {
  const { error } = props;
  const userPendingApproval = isErrorNoPermissionForUserPendingApproval(error);
  const userHasNoTransactionRights = isErrorNoPermissionForInitiateTransactions(error);

  // No transaction process attached to listing
  return error ? (
    <p className={css.error}>
      {error.message === 'No transaction process attached to listing' ? (
        <FormattedMessage id="RequestPriceForm.requestPriceErrorNoProcess" />
      ) : isTooManyRequestsError(error) ? (
        <FormattedMessage id="RequestPriceForm.tooManyRequestsError" />
      ) : userPendingApproval ? (
        <FormattedMessage id="RequestPriceForm.userPendingApprovalError" />
      ) : userHasNoTransactionRights ? (
        <FormattedMessage
          id="RequestPriceForm.noTransactionRightsError"
          values={{
            NoAccessLink: msg => (
              <NamedLink
                name="NoAccessPage"
                params={{ missingAccessRight: NO_ACCESS_PAGE_INITIATE_TRANSACTIONS }}
              >
                {msg}
              </NamedLink>
            ),
          }}
        />
      ) : (
        <FormattedMessage id="RequestPriceForm.requestPriceError" />
      )}
    </p>
  ) : null;
};

const LeadFeeRequestFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        submitButtonWrapperClassName,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        values,
        author,
        productDisplayName,
        leadFeeRequestError,
        intl,
      } = fieldRenderProps;

      const messageLabel = intl.formatMessage({
        id: 'LeadFeeRequestForm.messageLabel',
      });
      const messagePlaceholder = intl.formatMessage({
        id: 'LeadFeeRequestForm.messagePlaceholder',
      });

      const messageRequired = validators.requiredAndNonEmptyString('This field is required');
      const messageMaxLength = validators.maxLength(`Max ${MESSAGE_MAX_LENGTH} characters`, MESSAGE_MAX_LENGTH);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;

      const termsAndConditionsFromValues = values?.termsAndConditionsLeadFee || [];
      const isTermsAndConditionsAccepted = termsAndConditionsFromValues.length > 0;
      const submitDisabled = invalid || submitInProgress || !isTermsAndConditionsAccepted;

      return (
        <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="CartPage">
          <Heading as="h2" rootClassName={css.heading}>
            <Avatar className={css.avatar} user={author} />
            <FormattedMessage
              id="LeadFeeRequestForm.heading"
              values={{ productDisplayName }}
            />
          </Heading>

          <FieldTextInput
            className={css.field}
            inputRootClass={css.input}
            type="textarea"
            name="message"
            id={formId ? `${formId}.message` : 'message'}
            label={messageLabel}
            placeholder={messagePlaceholder}
            validate={validators.composeValidators(messageRequired, messageMaxLength)}
          />

          <TermsAndConditions />
          <div className={submitButtonWrapperClassName}>
            <ErrorMessage error={leadFeeRequestError} />
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="LeadFeeRequestForm.submitButtonText" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

LeadFeeRequestFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
  leadFeeRequestError: null,
};

LeadFeeRequestFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,
  inProgress: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]),
  productDisplayName: string.isRequired,
  leadFeeRequestError: propTypes.error,
  intl: intlShape.isRequired,
};

const LeadFeeRequestForm = compose(injectIntl)(LeadFeeRequestFormComponent);
LeadFeeRequestForm.displayName = 'LeadFeeRequestForm';

export default LeadFeeRequestForm;
